import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Typography, Tag, Space, Switch, Button, Table, InputNumber,
  Alert, Popconfirm, message, Tooltip,
} from 'antd';
import {
  PrinterOutlined, ReloadOutlined, CheckCircleOutlined,
  CloseCircleOutlined, LoadingOutlined, StopOutlined,
} from '@ant-design/icons';
import api from '../../api/client';

const { Title, Text } = Typography;

interface PostingCommand {
  id: number;
  type: string;
  workplace: string;
  order_id: number | null;
  document_kind: string;
  document_guid: string | null;
  status: string;
  result: any;
  error: string | null;
  created_by_id: number | null;
  created_at: string;
  taken_at: string | null;
  done_at: string | null;
}

interface Settings {
  flags: Record<string, boolean>;
  agent_seen_at: string | null;
}

const FLAG_LABELS: Record<string, string> = {
  'direct_posting.master': 'МАСТЕР-ВЫКЛЮЧАТЕЛЬ',
  'direct_posting.service_order': 'Проведение заказ-нарядов и реализаций',
  'direct_posting.receipt': 'Пробитие чеков ККМ',
  'direct_posting.print': 'Печать документов через 1С',
  'direct_posting.invoice': 'Создание счетов-фактур через 1С',
  'direct_posting.shift': 'Открытие/закрытие смены',
};

const TYPE_LABELS: Record<string, string> = {
  post_service_order: 'Провести ЗН',
  post_sale: 'Провести реализацию',
  fiscalize_receipt: 'Пробить чек',
  print_service_order: 'Печать ЗН',
  print_sales_receipt: 'Печать товарного чека',
  create_tax_invoice: 'Создать счёт-фактуру',
  print_tax_invoice: 'Печать счёта-фактуры',
  open_shift: 'Открыть смену',
  close_shift: 'Закрыть смену',
};

const STATUS_TAGS: Record<string, { color: string; label: string; icon?: React.ReactNode }> = {
  pending: { color: 'default', label: 'В очереди', icon: <LoadingOutlined /> },
  taken: { color: 'processing', label: 'У агента', icon: <LoadingOutlined /> },
  done: { color: 'success', label: 'Выполнено', icon: <CheckCircleOutlined /> },
  error: { color: 'error', label: 'Ошибка', icon: <CloseCircleOutlined /> },
  cancelled: { color: 'warning', label: 'Отменено', icon: <StopOutlined /> },
};

const KKTPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [commands, setCommands] = useState<PostingCommand[]>([]);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, me] = await Promise.all([
        api.get('/posting/settings'),
        api.get('/posting/commands', { params: { limit: 50 } }),
        api.get('/auth/me'),
      ]);
      setSettings(s.data);
      setCommands(c.data);
      const perms: string[] = me.data?.permissions || [];
      setIsAdmin(perms.includes('users:write'));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка загрузки');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const setFlag = async (key: string, enabled: boolean) => {
    try {
      const r = await api.put('/posting/settings', { key, enabled });
      setSettings(r.data);
      message.success(`${FLAG_LABELS[key]}: ${enabled ? 'ВКЛ' : 'ВЫКЛ'}`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось переключить');
    }
  };

  const sendCommand = async (type: string, needsOrder: boolean) => {
    if (needsOrder && !orderId) {
      message.warning('Укажите № заказа (внутренний id)');
      return;
    }
    setBusy(true);
    try {
      await api.post('/posting/commands', {
        type,
        order_id: needsOrder ? orderId : null,
      });
      message.success('Команда поставлена в очередь');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Команда отклонена');
    } finally {
      setBusy(false);
    }
  };

  const flags = settings?.flags || {};
  const masterOn = flags['direct_posting.master'];

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Команда', dataIndex: 'type', width: 160,
      render: (t: string) => TYPE_LABELS[t] || t,
    },
    { title: 'Заказ', dataIndex: 'order_id', width: 80, render: (v: number | null) => v ?? '—' },
    {
      title: 'Статус', dataIndex: 'status', width: 130,
      render: (s: string) => {
        const tag = STATUS_TAGS[s] || { color: 'default', label: s };
        return <Tag color={tag.color} icon={tag.icon}>{tag.label}</Tag>;
      },
    },
    {
      title: 'Результат / ошибка', dataIndex: 'error',
      render: (_: any, r: PostingCommand) =>
        r.error ? <Text type="danger">{r.error}</Text>
          : r.result ? <Text code>{JSON.stringify(r.result)}</Text> : '—',
    },
    {
      title: 'Создана', dataIndex: 'created_at', width: 170,
      render: (v: string) => v ? new Date(v).toLocaleString('ru-RU') : '—',
    },
  ];

  return (
    <div>
      <Title level={3}>
        <PrinterOutlined style={{ marginRight: 8 }} />
        Касса — прямое проведение документов
      </Title>

      {masterOn && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="ПРЯМОЕ ПРОВЕДЕНИЕ ВКЛЮЧЕНО"
          description="Команды из AR_DB выполняются 1С на рабочем месте кассира. Не забудьте выключить после теста."
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="Выключатели (kill switch)" size="small"
          extra={<Text type="secondary">
            Утилита на связи: {settings?.agent_seen_at
              ? new Date(settings.agent_seen_at).toLocaleString('ru-RU')
              : 'нет данных'}
          </Text>}>
          <Space direction="vertical" size="small">
            {Object.keys(FLAG_LABELS).map((key) => (
              <Space key={key}>
                {isAdmin ? (
                  <Popconfirm
                    title={`${flags[key] ? 'Выключить' : 'Включить'}: ${FLAG_LABELS[key]}?`}
                    onConfirm={() => setFlag(key, !flags[key])}
                  >
                    <Switch checked={!!flags[key]} />
                  </Popconfirm>
                ) : (
                  <Switch checked={!!flags[key]} disabled />
                )}
                <Text strong={key === 'direct_posting.master'}>{FLAG_LABELS[key]}</Text>
                {key === 'direct_posting.master' && masterOn && (
                  <Tag color="warning">ВКЛЮЧЕНО — не забудьте выключить</Tag>
                )}
              </Space>
            ))}
            {!isAdmin && (
              <Text type="secondary">Переключение доступно только администратору.</Text>
            )}
          </Space>
        </Card>

        <Card title="Команды (тестовая панель)" size="small">
          <Space wrap>
            <Tooltip title="Внутренний id заказа из AR_DB">
              <InputNumber
                placeholder="№ заказа" min={1} value={orderId}
                onChange={(v) => setOrderId(v)}
              />
            </Tooltip>
            <Button disabled={!flags['direct_posting.service_order'] || busy}
              onClick={() => sendCommand('post_service_order', true)}>
              Провести ЗН
            </Button>
            <Button disabled={!flags['direct_posting.service_order'] || busy}
              onClick={() => sendCommand('post_sale', true)}>
              Провести реализацию
            </Button>
            <Button disabled={!flags['direct_posting.receipt'] || busy}
              onClick={() => sendCommand('fiscalize_receipt', true)}>
              Пробить чек
            </Button>
            <Button disabled={!flags['direct_posting.shift'] || busy}
              onClick={() => sendCommand('open_shift', false)}>
              Открыть смену
            </Button>
            <Button disabled={!flags['direct_posting.shift'] || busy}
              onClick={() => sendCommand('close_shift', false)}>
              Закрыть смену
            </Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Кнопки активны только при включённом соответствующем выключателе.
            </Text>
          </div>
        </Card>

        <Card title="Журнал команд" size="small"
          extra={<Button icon={<ReloadOutlined />} onClick={load} />}>
          <Table
            dataSource={commands} columns={columns} rowKey="id"
            size="small" pagination={false}
            locale={{ emptyText: 'Команд пока не было' }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default KKTPage;
